import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  configureAllVueOAuth,
  configureAllVueSnowflake,
  fetchAllVueConfig,
} from '../../../services/queries/MaggieIntegrationsQueries';
import { AllVueFormValues } from './allVueConfig.schema';

export function useAllVueConfigActions() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['allvue-config'],
    queryFn: fetchAllVueConfig,
  });

  const snowflakeMutation = useMutation({
    mutationFn: configureAllVueSnowflake,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allvue-config'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const oauthMutation = useMutation({
    mutationFn: configureAllVueOAuth,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allvue-config'] });
      queryClient.invalidateQueries({ queryKey: ['integrations'] });
    },
  });

  const config = data ?? null;
  const isConnected = config?.isConnected ?? false;
  const serverConnectionType = config?.connectionType ?? 'not_configured';

  const saveConfig = useCallback(
    async (values: AllVueFormValues) => {
      if (values.connectionType === 'snowflake') {
        return await snowflakeMutation.mutateAsync({
          database: values.database.trim(),
        });
      } else {
        return await oauthMutation.mutateAsync({
          clientId: values.clientId.trim(),
          clientSecret: values.clientSecret.trim() || undefined,
          tokenEndpoint: values.tokenEndpoint.trim() || undefined,
        });
      }
    },
    [snowflakeMutation, oauthMutation]
  );

  const resetConfig = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['allvue-config'] });
  }, [queryClient]);

  return { config, isConnected, serverConnectionType, isLoading, saveConfig, resetConfig };
}
